import { Button, Spin } from "antd";
import { useApiUrl, useCustomMutation, useNotification } from "@refinedev/core";
import { FaRegEnvelope } from "react-icons/fa";
import React from "react";
import axios from "axios";

interface EmailIncrementalUpdateProps {
  imoNumber: string;
  disabled?: boolean;
}

export const EmailIncrementalUpdate: React.FC<EmailIncrementalUpdateProps> = ({
  imoNumber,
  disabled = false
}) => {
  const [loading, setLoading] = React.useState(false);
  const { open } = useNotification();
  const apiUrl = useApiUrl();
  const { mutate } = useCustomMutation();

  const triggerEmail = async () => {
    setLoading(true);
    try {
      // Step 1: GET pending incremental updates
      const response = await axios.get(
        `${apiUrl}/batch-ess-update/pending-incremental-updates/${imoNumber}`
      );

      if (!response.data?.hasPendingUpdate) {
        open?.({ type: "error", message: "No pending updates for this vessel" });
        setLoading(false);
        return;
      }

      // Step 2: POST trigger bulk email
      mutate(
        {
          url: `${apiUrl}/bulk-email/incremental-update`,
          method: "post",
          values: {
            imoNumber: imoNumber,
            requestId: response.data.requestId,
          },
          // We show our own notifications below (onSuccess/onError), so tell
          // useCustomMutation not to show its built-in ones too. Without
          // this, a failed request shows BOTH our actionable message AND
          // refine's generic "Request failed with status code 409 / Error
          // (status code: undefined)" toast.
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => {
            open?.({ type: "success", message: "Email triggered successfully" });
            setLoading(false);
          },
          // The API refuses with 409 and an actionable sentence when the update
          // was stored under an unworkable max email size ("Raise the vessel's
          // Max Email Size and run a fresh incremental update"). That text is in
          // the response body, not on the error object: useCustomMutation hands
          // the raw AxiosError through untouched, and the response interceptor
          // that maps `message` belongs to @refinedev/nestjsx-crud's own axios
          // instance, which this call does not use. So `error.message` here is
          // only "Request failed with status code 409".
          onError: (error: any) => {
            const apiMessage = error?.response?.data?.message;
            open?.({
              type: "error",
              message: "Failed to trigger email",
              description: apiMessage || error?.message || "An error occurred",
            });
            setLoading(false);
          },
        }
      );
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      open?.({
        type: "error",
        message: "Failed to fetch pending updates",
        description: apiMessage || error?.message || "An error occurred",
      });
      setLoading(false);
    }
  };

  return loading ? <Spin size="small" /> : (
    <Button
      type="default"
      size="small"
      disabled={disabled || loading}
      onClick={triggerEmail}
      icon={<FaRegEnvelope />}
    />
  );
};
